import React, { useMemo } from 'react';
import { assertNever, keyBy } from '@varos/util-typescript';
import { HiOutlineArrowUturnLeft, HiOutlineArrowUturnRight } from 'react-icons/hi2';
import { HiOutlineClipboardCopy } from 'react-icons/hi';
import { createStateHistoryController } from '@varos/react-undo';
import { FormStatus } from '@varos/research-sdk-api';
import { ReportingControllerBaseConfig } from '../../../../base';
import { FormBuilderController } from './formBuilderInterface';
import { FormBuilderControllerProps, FormBuilderViewProps } from './formBuilderProps';
import { FieldTypeItem, FieldTypeItemProps } from '../../../common/type';
import { DocumentFormFieldValues, DocumentFormFormValues } from './state';

function getSaveButtonLabel(status: FormStatus): string {
    if (status === 'draft') {
        return 'Save changes';
    }
    if (status === 'published') {
        return 'Save changes';
    }
    if (status === 'archived') {
        return 'Save changes';
    }
    assertNever(status);
}

export function createFormBuilderController(
    config: ReportingControllerBaseConfig
): FormBuilderController {
    const {
        kernel: {
            infra: { formatter },
            provider: { createFormController },
        },
    } = config;
    const formController = createFormController();

    // Create state history controller for form state management
    const stateHistoryController = createStateHistoryController<DocumentFormFormValues>({
        maxHistory: 50, // Keep last 50 changes
        // Exclude text input paths from undo/redo tracking - let browser handle native input history
        excludePaths: [
            'fields.*.title', // Field titles
            'fields.*.description', // Field descriptions
            'fields.*.options.*.label', // Option labels
            'fields.*.options.*.value', // Option values
        ],
        // shouldSkipHistory: (current, previous) => {
        //     // Skip history if only whitespace changes or very small changes
        //     return JSON.stringify(current) === JSON.stringify(previous);
        // },
    });

    // function buildTypeOptionProps(
    //     props: FormBuilderControllerProps,
    //     state: DocumentFormFieldValues,
    //     item: FieldTypeItem
    // ): FieldTypeItemProps {
    //     return {
    //         id: item.value,
    //         Icon: item.Icon,
    //         value: item.value,
    //         label: item.displayName,
    //         onClick() {
    //             const updatedField = { ...state, type: item.value };

    //             // Initialize default options for enum fields
    //             if (item.value === 'enum' && !state.options) {
    //                 updatedField.options = [
    //                     {
    //                         id: crypto.randomUUID(),
    //                         value: 'Option 1',
    //                         label: 'Option 1',
    //                     },
    //                     {
    //                         id: crypto.randomUUID(),
    //                         value: 'Option 2',
    //                         label: 'Option 2',
    //                     },
    //                 ];
    //             }

    //             // Clear options for non-enum fields
    //             if (item.value !== 'enum' && state.options) {
    //                 updatedField.options = undefined;
    //             }

    //             props.field.onUpdate(updatedField);
    //         },
    //     };
    // }

    return {
        useProps(context, item, props): FormBuilderViewProps {
            const form = formController.useProps(props.form);

            // Integrate state history with form values
            const stateHistory = stateHistoryController.useProps({
                value: props.values,
                onUpdate: (newValues) => {
                    // Update the form with new values from history
                    props.form.form.setValue('fields', newValues.fields, {
                        shouldDirty: true,
                    });
                },
            });

            const optionsByValue = useMemo(() => {
                return keyBy(props.field.tone.options, 'value');
            }, [props.field.tone.options]);

            return {
                form: {
                    groups: [
                        {
                            id: 'content',
                            label: 'Content',
                            description:
                                'Provide details about the interview scope and goals',
                            fields: [
                                {
                                    id: 'context',
                                    label: 'Context',
                                    description:
                                        'Provide relevant context to customize the interview experience',
                                    element: {
                                        kind: 'textarea',
                                        input: {
                                            placeholder: 'Describe scope',
                                            value: props.values.context || '',
                                            onChange(event) {
                                                props.form.form.setValue(
                                                    'context',
                                                    event.target.value,
                                                    {
                                                        shouldDirty: true,
                                                    }
                                                );
                                            },
                                        },
                                    },
                                },
                                {
                                    id: 'goals',
                                    label: 'Goals',
                                    description:
                                        'Describe one or more goals to help the agent tailor the interview to your use-case',
                                    element: {
                                        kind: 'textarea',
                                        input: {
                                            placeholder: 'Describe goals',
                                            value: props.values.goals || '',
                                            onChange(event) {
                                                props.form.form.setValue(
                                                    'goals',
                                                    event.target.value,
                                                    {
                                                        shouldDirty: true,
                                                    }
                                                );
                                            },
                                        },
                                    },
                                },
                            ],
                        },
                        {
                            id: 'agent',
                            label: 'Agent',
                            description: 'Customize the interviewer to fit your use-case',
                            fields: [
                                {
                                    id: 'name',
                                    label: 'Name',
                                    description: null,
                                    element: {
                                        kind: 'input',
                                        input: {
                                            placeholder: 'Eg. Arthur',
                                            value: props.values.agentName || '',
                                            onChange(event) {
                                                props.form.form.setValue(
                                                    'agentName',
                                                    event.target.value,
                                                    {
                                                        shouldDirty: true,
                                                    }
                                                );
                                            },
                                        },
                                    },
                                },
                                {
                                    id: 'tone',
                                    label: 'Tone',
                                    description:
                                        'Customize the personality and tone of the interviewer',
                                    element: {
                                        kind: 'multi_select',
                                        input: {
                                            value:
                                                props.values.tone?.flatMap((value) =>
                                                    optionsByValue[value]
                                                        ? [optionsByValue[value]]
                                                        : []
                                                ) ?? [],
                                            onChange(values) {
                                                props.form.form.setValue(
                                                    'tone',
                                                    values.map((value) => value.value),
                                                    {
                                                        shouldDirty: true,
                                                    }
                                                );
                                            },
                                            placeholder: 'Select tone',
                                            isMulti: true,
                                            menuShouldScrollIntoView: false,
                                            closeMenuOnSelect: false,
                                            isSearchable: false,
                                            options: props.field.tone.options,
                                        },
                                    },
                                },
                            ],
                        },
                        {
                            id: 'presentation',
                            label: 'Presentation',
                            description: `Customize the interview landing page for respondents`,
                            fields: [
                                {
                                    id: 'title',
                                    label: 'Title',
                                    description: null,
                                    element: {
                                        kind: 'input',
                                        input: {
                                            placeholder: 'Enter title',
                                            value: props.values.title || '',
                                            onChange(event) {
                                                props.form.form.setValue(
                                                    'title',
                                                    event.target.value,
                                                    {
                                                        shouldDirty: true,
                                                    }
                                                );
                                            },
                                        },
                                    },
                                },
                                {
                                    id: 'description',
                                    label: 'Description',
                                    description: `A brief description of the interview`,
                                    element: {
                                        kind: 'textarea',
                                        input: {
                                            value: props.values.description || '',
                                            placeholder: 'Enter description',
                                            onChange(event) {
                                                props.form.form.setValue(
                                                    'description',
                                                    event.target.value,
                                                    {
                                                        shouldDirty: true,
                                                    }
                                                );
                                            },
                                        },
                                    },
                                },
                            ],
                        },
                    ],
                },
                field: {
                    form: {
                        id: item.form.id,
                    },
                    value: {
                        fields: props.values.fields,
                    },
                    onChange(newFields) {
                        props.form.form.setValue('fields', newFields.fields, {
                            shouldDirty: true,
                        });
                    },
                },
                actions: [
                    {
                        id: 'undo',
                        Icon: HiOutlineArrowUturnLeft,
                        tooltip: {
                            label: stateHistory.canUndo ? 'Undo (⌘Z)' : 'Nothing to undo',
                        },
                        button: {
                            onClick: stateHistory.undo,
                            isDisabled: !stateHistory.canUndo,
                        },
                    },
                    {
                        id: 'redo',
                        Icon: HiOutlineArrowUturnRight,
                        tooltip: {
                            label: stateHistory.canRedo
                                ? 'Redo (⌘⇧Z)'
                                : 'Nothing to redo',
                        },
                        button: {
                            onClick: stateHistory.redo,
                            isDisabled: !stateHistory.canRedo,
                        },
                    },
                ],
                menu: {
                    popover: {
                        isLazy: true,
                        placement: 'bottom-end',
                    },
                    items: [
                        {
                            kind: 'link',
                            label: 'Form ID',
                            link: {
                                value: item.form.id,
                                placeholder: 'Form ID',
                            },
                        },
                    ],
                },
                isDirty: props.form.form.formState.isDirty,
                share: {
                    button: {
                        label: 'Share interview',
                    },
                    popover: {
                        form: {
                            id: item.form.id,
                        },
                    },
                },
                save: {
                    label: getSaveButtonLabel(item.form.status),
                    button: {
                        onClick: props.form.form.handleSubmit(props.form.onSubmit),
                        isLoading: props.form.form.formState.isSubmitting,
                        isDisabled: !props.form.form.formState.isDirty,
                    },
                },
                reset: {
                    button: {
                        onClick: () => {
                            props.form.form.reset();
                        },
                        isLoading: false,
                        isDisabled: false,
                    },
                    isVisible: props.form.form.formState.isDirty,
                },
            };
        },
    };
}
